/**
 * Kontoauszuege im Format CAMT.053 (ISO 20022) einlesen — Grundlage des
 * Zahlungsimports (M8).
 *
 * Die Funktion ist bewusst pur: sie liest einen String und gibt Daten zurueck.
 * Nichts wird gebucht, nichts wird gespeichert. Das Buchen entscheidet ein
 * Mensch anhand der Vorschlaege aus matchPayments.ts.
 *
 * Unterstuetzt werden camt.053.001.02 (bis heute der Export der Hamburger
 * Sparkasse) und camt.053.001.08 (Nachfolgeversion). Die Elementnamen sind in
 * beiden Versionen weitgehend gleich; die Unterschiede, die uns betreffen,
 * sind die Partei-Verschachtelung (v8: RltdPties/Dbtr/Pty/Nm statt
 * RltdPties/Dbtr/Nm) und der Buchungsstatus (v8: Sts/Cd statt Sts). Beides
 * wird hier abgefangen, deshalb wird nur die Hauptversion camt.053.001
 * geprueft und nicht die Unterversion.
 *
 * Dazu kommt die Umsatzinformation camt.052.001 — das, was das
 * Haspa-Onlinebanking aus der Umsatzanzeige als XML exportiert. Die Umsaetze
 * (<Ntry>) sind dort genauso aufgebaut, nur die Huelle heisst anders
 * (BkToCstmrAcctRpt/Rpt statt BkToCstmrStmt/Stmt). Weil eine Umsatzinformation
 * auch noch nicht endgueltige Umsaetze fuehrt, gilt dort nur ein ausdruecklich
 * gebuchter Umsatz (Sts = BOOK) als gebucht.
 *
 * Die Bankreferenz (AcctSvcrRef, bei der Haspa die FI-UMSATZ-ID) ist in zwei
 * Exporten mit ueberlappendem Zeitraum fuer denselben Umsatz gleich — an ihr
 * erkennt der Import, was schon gebucht ist. Ob der Kontoauszug camt.053
 * dieselbe Referenz traegt, ist nicht belegt; die beiden Formate sollten
 * deshalb nicht gemischt werden.
 */

import { Cents, roundCents } from '../money';

// ═══════════════════════════════════════════════════════════════════════════
// Ergebnistypen
// ═══════════════════════════════════════════════════════════════════════════

export interface BankTransaction {
    /**
     * Idempotenzschluessel fuer invoice_payments.bank_tx_id.
     *
     * Muss fuer denselben Umsatz bei jedem Einlesen derselben Datei identisch
     * sein — nur so verhindert die Eindeutigkeit in der Datenbank, dass ein
     * zweimal hochgeladener Auszug denselben Zahlungseingang zweimal bucht.
     */
    bankTxId: string;
    /** SEPA-Ende-zu-Ende-Referenz, sofern der Zahler eine gesetzt hat. */
    endToEndId?: string;
    /** Wertstellung als ISO-Datum (yyyy-mm-dd). */
    valueDate: string;
    /** Buchungsdatum, falls abweichend von der Wertstellung. */
    bookingDate?: string;
    /** Vorzeichen aus CdtDbtInd: Eingang positiv, Ausgang (DBIT) negativ. */
    amountCents: Cents;
    currency: string;
    /** Verwendungszweck, aus allen Ustrd-Zeilen zusammengesetzt. */
    remittanceInfo: string;
    counterpartyName?: string;
    counterpartyIban?: string;
    /**
     * False bei vorgemerkten Umsaetzen (Sts = PDNG). Die sind noch nicht
     * endgueltig und duerfen nicht gebucht werden.
     */
    booked: boolean;
    /** Bankreferenz des Umsatzes, falls die Bank eine mitgeliefert hat. */
    accountServicerReference?: string;
}

export interface BankStatement {
    /** Kontoauszug (camt.053) oder Umsatzinformation (camt.052). */
    format: 'camt.053' | 'camt.052';
    /**
     * Eine Id je <Stmt> bzw. <Rpt> in der Datei. Eine Datei kann mehrere Auszuege
     * enthalten (z.B. mehrere Tage) — die Umsaetze werden zusammengefasst,
     * die Auszugsnummern bleiben aber einzeln erhalten, weil der Anwender in
     * der Oberflaeche wissen muss, was er gerade importiert.
     */
    statementIds: string[];
    iban?: string;
    currency?: string;
    /** Erstellungszeitpunkt des Auszugs (GrpHdr/CreDtTm), unveraendert. */
    createdAt?: string;
    /** Anfangssaldo des ersten Auszugs. */
    openingBalanceCents?: Cents;
    /** Endsaldo des letzten Auszugs — Pruefsumme fuer die Oberflaeche. */
    closingBalanceCents?: Cents;
    transactions: BankTransaction[];
}

// ═══════════════════════════════════════════════════════════════════════════
// XML-Abstraktion
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der Ausschnitt der DOM-Schnittstelle, den dieses Modul braucht.
 *
 * Warum eine eigene Schnittstelle statt direkt DOMParser: die Anwendung laeuft
 * im Browser, die Tests laufen unter Node ohne DOM. Ein globaler Zugriff auf
 * DOMParser waere im Test nicht ausfuehrbar, und die Auswertung der Struktur —
 * also der Teil, in dem Geldbetraege und Vorzeichen entstehen — bliebe damit
 * ungeprueft. Ueber diese Schnittstelle laeuft beides durch denselben Code.
 *
 * Ein echter DOMParser erfuellt sie strukturell, ohne dass hier etwas
 * angepasst werden muesste.
 */
export interface XmlElementCollectionLike {
    readonly length: number;
    item(index: number): XmlElementLike | null;
}

export interface XmlElementLike {
    readonly localName: string;
    readonly namespaceURI: string | null;
    readonly textContent: string | null;
    readonly children: XmlElementCollectionLike;
    getAttribute(qualifiedName: string): string | null;
}

export interface XmlDocumentLike {
    readonly documentElement: XmlElementLike | null;
}

export interface XmlParserLike {
    parseFromString(source: string, mimeType: string): XmlDocumentLike;
}

const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
};

const decodeEntities = (text: string): string =>
    text.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*);/g, (match, body: string) => {
        if (body.startsWith('#x') || body.startsWith('#X')) {
            const code = Number.parseInt(body.slice(2), 16);
            return Number.isNaN(code) ? match : String.fromCodePoint(code);
        }
        if (body.startsWith('#')) {
            const code = Number.parseInt(body.slice(1), 10);
            return Number.isNaN(code) ? match : String.fromCodePoint(code);
        }
        return NAMED_ENTITIES[body] ?? match;
    });

/** Knoten des Ersatz-Parsers. Haelt nur, was XmlElementLike verlangt. */
class MiniElement implements XmlElementLike {
    readonly localName: string;
    readonly qualifiedName: string;
    readonly namespaceURI: string | null;
    private readonly attributes: Map<string, string>;
    private readonly elementChildren: MiniElement[] = [];
    // Text und Kinder in Dokumentreihenfolge, damit textContent auch bei
    // gemischtem Inhalt die richtige Zeichenfolge ergibt.
    private readonly nodes: Array<string | MiniElement> = [];

    constructor(
        qualifiedName: string,
        localName: string,
        namespaceURI: string | null,
        attributes: Map<string, string>,
    ) {
        this.qualifiedName = qualifiedName;
        this.localName = localName;
        this.namespaceURI = namespaceURI;
        this.attributes = attributes;
    }

    appendText(value: string): void {
        this.nodes.push(value);
    }

    appendChild(element: MiniElement): void {
        this.nodes.push(element);
        this.elementChildren.push(element);
    }

    get children(): XmlElementCollectionLike {
        const list = this.elementChildren;
        return {
            length: list.length,
            item: (index: number) => list[index] ?? null,
        };
    }

    get textContent(): string {
        return this.nodes.map((node) => (typeof node === 'string' ? node : node.textContent)).join('');
    }

    getAttribute(qualifiedName: string): string | null {
        return this.attributes.get(qualifiedName) ?? null;
    }
}

/** Ende eines Tags suchen, ohne auf ein '>' innerhalb eines Attributwerts hereinzufallen. */
const findTagEnd = (source: string, start: number): number => {
    let quote: string | null = null;
    for (let i = start; i < source.length; i++) {
        const character = source[i];
        if (quote) {
            if (character === quote) quote = null;
            continue;
        }
        if (character === '"' || character === "'") {
            quote = character;
            continue;
        }
        if (character === '>') return i;
    }
    return -1;
};

const parseAttributes = (raw: string): Map<string, string> => {
    const out = new Map<string, string>();
    const pattern = /([^\s=/<>]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
    let match = pattern.exec(raw);
    while (match) {
        out.set(match[1], decodeEntities(match[3] ?? match[4] ?? ''));
        match = pattern.exec(raw);
    }
    return out;
};

/**
 * Ersatz-Parser fuer Umgebungen ohne DOM.
 *
 * Deckt bewusst nur das ab, was in einem maschinell erzeugten CAMT-Export
 * vorkommt: Deklaration, Kommentare, CDATA, Elemente mit Attributen,
 * Namensraeume und Text. Alles Weitergehende (DTD-Entitaeten, Verarbeitung
 * externer Verweise) wird ignoriert oder fuehrt zu einem Fehler — ein
 * Kontoauszug, der so etwas enthaelt, soll nicht stillschweigend
 * durchgewinkt werden.
 */
const parseXmlSubset = (source: string): XmlDocumentLike => {
    let root: MiniElement | null = null;
    const stack: MiniElement[] = [];
    const scopes: Array<Map<string, string>> = [];
    let index = 0;

    const lookupNamespace = (prefix: string): string | null => {
        for (let level = scopes.length - 1; level >= 0; level--) {
            const uri = scopes[level].get(prefix);
            if (uri !== undefined) return uri;
        }
        return null;
    };

    while (index < source.length) {
        const start = source.indexOf('<', index);
        if (start < 0) break;
        if (start > index && stack.length) {
            stack[stack.length - 1].appendText(decodeEntities(source.slice(index, start)));
        }

        if (source.startsWith('<!--', start)) {
            const end = source.indexOf('-->', start + 4);
            if (end < 0) throw new Error('unbeendeter Kommentar');
            index = end + 3;
            continue;
        }
        if (source.startsWith('<![CDATA[', start)) {
            const end = source.indexOf(']]>', start + 9);
            if (end < 0) throw new Error('unbeendeter CDATA-Abschnitt');
            if (stack.length) stack[stack.length - 1].appendText(source.slice(start + 9, end));
            index = end + 3;
            continue;
        }
        if (source.startsWith('<?', start)) {
            const end = source.indexOf('?>', start + 2);
            if (end < 0) throw new Error('unbeendete Verarbeitungsanweisung');
            index = end + 2;
            continue;
        }
        if (source.startsWith('<!', start)) {
            const end = findTagEnd(source, start);
            if (end < 0) throw new Error('unbeendete Deklaration');
            index = end + 1;
            continue;
        }

        const end = findTagEnd(source, start);
        if (end < 0) throw new Error('unbeendetes Element');
        const inner = source.slice(start + 1, end).trim();

        if (inner.startsWith('/')) {
            const name = inner.slice(1).trim();
            const current = stack.pop();
            scopes.pop();
            if (!current) throw new Error(`schliessendes Element </${name}> ohne oeffnendes`);
            if (current.qualifiedName !== name) {
                throw new Error(`</${name}> schliesst <${current.qualifiedName}> nicht`);
            }
            index = end + 1;
            continue;
        }

        const selfClosing = inner.endsWith('/');
        const body = selfClosing ? inner.slice(0, -1) : inner;
        const nameMatch = /^([^\s/>]+)/.exec(body);
        if (!nameMatch) throw new Error('Element ohne Namen');
        const qualifiedName = nameMatch[1];
        const attributes = parseAttributes(body.slice(qualifiedName.length));

        // Der eigene Geltungsbereich muss vor der Aufloesung stehen: ein
        // Element darf den Namensraum benutzen, den es selbst deklariert.
        const scope = new Map<string, string>();
        attributes.forEach((value, key) => {
            if (key === 'xmlns') scope.set('', value);
            else if (key.startsWith('xmlns:')) scope.set(key.slice(6), value);
        });
        scopes.push(scope);

        const colon = qualifiedName.indexOf(':');
        const prefix = colon < 0 ? '' : qualifiedName.slice(0, colon);
        const localName = colon < 0 ? qualifiedName : qualifiedName.slice(colon + 1);
        const element = new MiniElement(
            qualifiedName,
            localName,
            lookupNamespace(prefix),
            attributes,
        );

        const parent = stack[stack.length - 1];
        if (parent) parent.appendChild(element);
        else if (root) throw new Error('mehr als ein Wurzelelement');
        else root = element;

        if (selfClosing) scopes.pop();
        else stack.push(element);

        index = end + 1;
    }

    if (stack.length) {
        throw new Error(`nicht geschlossenes Element <${stack[stack.length - 1].qualifiedName}>`);
    }
    return { documentElement: root };
};

/** Parser ohne DOM — im Test der Standard, im Browser die Rueckfallebene. */
export const fallbackXmlParser: XmlParserLike = {
    parseFromString: (source: string) => parseXmlSubset(source),
};

/**
 * Im Browser der eingebaute DOMParser, sonst der Ersatz.
 *
 * Der DOMParser bleibt im Browser die erste Wahl, weil er einen mehrere
 * Megabyte grossen Jahresauszug deutlich schneller verarbeitet als eine
 * Schleife in JavaScript.
 */
export const defaultXmlParser = (): XmlParserLike =>
    typeof DOMParser === 'undefined' ? fallbackXmlParser : new DOMParser();

// ═══════════════════════════════════════════════════════════════════════════
// Navigation im Baum
// ═══════════════════════════════════════════════════════════════════════════

const childElements = (element: XmlElementLike): XmlElementLike[] => {
    const out: XmlElementLike[] = [];
    for (let i = 0; i < element.children.length; i++) {
        const child = element.children.item(i);
        if (child) out.push(child);
    }
    return out;
};

/**
 * Kinder werden ueber localName gesucht, nie ueber den vollen Namen: manche
 * Institute exportieren mit Praefix (<ns:Ntry>), andere ohne.
 */
const childNamed = (element: XmlElementLike | null, name: string): XmlElementLike | null =>
    element ? (childElements(element).find((child) => child.localName === name) ?? null) : null;

const childrenNamed = (element: XmlElementLike | null, name: string): XmlElementLike[] =>
    element ? childElements(element).filter((child) => child.localName === name) : [];

const atPath = (element: XmlElementLike | null, ...names: string[]): XmlElementLike | null =>
    names.reduce<XmlElementLike | null>((node, name) => childNamed(node, name), element);

const textOf = (element: XmlElementLike | null): string => element?.textContent?.trim() ?? '';

const textAt = (element: XmlElementLike | null, ...names: string[]): string =>
    textOf(atPath(element, ...names));

/** Erster nicht leerer Wert — fuer Felder, die je nach Version woanders stehen. */
const firstNonEmpty = (...values: Array<string | undefined>): string =>
    values.find((value) => !!value && value.trim().length > 0)?.trim() ?? '';

// ═══════════════════════════════════════════════════════════════════════════
// Betraege, Datum, Schluessel
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Betrag aus dem XML nach Cent.
 *
 * Bewusst NICHT centsFromEuroInput: das ist der Parser fuer Benutzereingaben
 * und liest "1.000" als eintausend, weil der Punkt in einem deutschen Formular
 * ein Tausenderpunkt ist. In ISO 20022 ist der Punkt immer das Dezimaltrennzeichen
 * und "1.000" damit ein Euro. Ein Faktor 1000 an dieser Stelle waere der
 * teuerste denkbare Fehler des Moduls.
 */
const parseXmlAmount = (raw: string, context: string): Cents => {
    const text = raw.trim();
    if (!/^-?\d+(\.\d+)?$/.test(text)) {
        throw new Error(`Der Betrag "${raw}" im Kontoauszug ist nicht lesbar (${context}).`);
    }
    // Betrag OHNE Vorzeichen. ISO 20022 laesst in Amt keine negativen Werte zu;
    // die Richtung steht ausschliesslich in CdtDbtInd. Ein negativ notierter
    // Betrag zusammen mit DBIT ergaebe sonst die doppelte Verneinung, die aus
    // einem Abgang einen Eingang macht.
    return Math.abs(roundCents(Number(text) * 100));
};

/**
 * ISO-Datum aus <Dt> oder <DtTm>.
 *
 * Bei DtTm wird nur der Datumsteil uebernommen und die Zeitzone ignoriert.
 * Eine Umrechnung nach UTC koennte das Wertstellungsdatum um einen Tag
 * verschieben — die Bank meint aber den Kalendertag, den sie hingeschrieben hat.
 */
const toIsoDate = (raw: string, context: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (!match) {
        throw new Error(`Das Datum "${raw}" im Kontoauszug ist nicht lesbar (${context}).`);
    }
    return `${match[1]}-${match[2]}-${match[3]}`;
};

/** Datum aus einem Knoten, der entweder <Dt> oder <DtTm> enthaelt. */
const dateFrom = (element: XmlElementLike | null, context: string): string => {
    const raw = firstNonEmpty(textAt(element, 'Dt'), textAt(element, 'DtTm'));
    return raw ? toIsoDate(raw, context) : '';
};

/**
 * FNV-1a, zweimal mit unterschiedlichem Startwert und Leserichtung, ergibt 64
 * Bit als Hex.
 *
 * Kein SHA-256: das steht im Browser nur asynchron zur Verfuegung (crypto.subtle),
 * und parseCamt053 soll eine pure, synchrone Funktion bleiben. Kryptographisch
 * muss der Wert nichts leisten — er ist kein Geheimnis, sondern ein
 * Wiedererkennungsmerkmal. Eine Kollision waere ausserdem nicht still: die
 * Datenbank haelt bank_tx_id eindeutig, und der Import meldet den Posten dann
 * als bereits gebucht, statt ihn zu verdoppeln.
 */
const fnv1a32 = (text: string, seed: number, reverse: boolean): number => {
    let hash = seed >>> 0;
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(reverse ? text.length - 1 - i : i);
        hash = Math.imul(hash ^ (code & 0xff), 16777619);
        hash = Math.imul(hash ^ ((code >>> 8) & 0xff), 16777619);
    }
    return hash >>> 0;
};

export const stableTransactionHash = (content: string): string =>
    fnv1a32(content, 0x811c9dc5, false).toString(16).padStart(8, '0') +
    fnv1a32(content, 0x9dc5811c, true).toString(16).padStart(8, '0');

// ═══════════════════════════════════════════════════════════════════════════
// Auswertung
// ═══════════════════════════════════════════════════════════════════════════

const CAMT053_NAMESPACE_PREFIX = 'urn:iso:std:iso:20022:tech:xsd:camt.053.001.';
const CAMT052_NAMESPACE_PREFIX = 'urn:iso:std:iso:20022:tech:xsd:camt.052.001.';

export interface Camt053Options {
    /**
     * Parser fuer das XML. Ohne Angabe der DOMParser des Browsers bzw. der
     * eingebaute Ersatz.
     */
    xmlParser?: XmlParserLike;
}

const hasParserError = (root: XmlElementLike): boolean =>
    root.localName === 'parsererror' ||
    childElements(root).some((child) => child.localName === 'parsererror');

/**
 * Name und IBAN der Gegenseite.
 *
 * Wer die Gegenseite ist, haengt an der Richtung: bei einem Eingang ist es der
 * Zahler (Dbtr), bei einem Ausgang der Empfaenger (Cdtr). In camt.053.001.08
 * liegt der Name eine Ebene tiefer unter <Pty>, deshalb beide Wege.
 */
const counterpartyFrom = (
    parties: XmlElementLike | null,
    incoming: boolean,
): { name: string; iban: string } => {
    const partyTag = incoming ? 'Dbtr' : 'Cdtr';
    const accountTag = incoming ? 'DbtrAcct' : 'CdtrAcct';
    const party = childNamed(parties, partyTag);
    return {
        name: firstNonEmpty(textAt(party, 'Nm'), textAt(party, 'Pty', 'Nm')),
        iban: textAt(parties, accountTag, 'Id', 'IBAN'),
    };
};

/**
 * Verwendungszweck aus den Ustrd-Zeilen.
 *
 * Die Sparkasse zerlegt den Zweck in Bloecke zu 27 Zeichen. Zusammengefuegt
 * wird mit Leerzeichen: das kann ein an der Blockgrenze getrenntes Wort
 * auseinanderziehen, aber ein Zusammenkleben ohne Trenner wuerde
 * Rechnungsnummer und Folgewort verschmelzen — und genau die Rechnungsnummer
 * ist das, wonach matchPayments sucht.
 */
const remittanceFrom = (details: XmlElementLike | null, entry: XmlElementLike): string => {
    const lines = childrenNamed(atPath(details, 'RmtInf'), 'Ustrd')
        .map((line) => textOf(line))
        .filter((line) => line.length > 0);
    const joined = lines.join(' ').replace(/\s+/g, ' ').trim();
    // AddtlNtryInf ist die Rueckfallebene: manche Institute stellen den Zweck
    // nur dort ein, wenn kein strukturierter SEPA-Datensatz vorliegt.
    return joined || textAt(entry, 'AddtlNtryInf');
};

/** Buchungsstatus: v2 schreibt <Sts>BOOK</Sts>, v8 <Sts><Cd>BOOK</Cd></Sts>. */
const statusCodeOf = (entry: XmlElementLike): string => {
    const status = childNamed(entry, 'Sts');
    if (!status) return '';
    return firstNonEmpty(textAt(status, 'Cd'), textOf(status)).toUpperCase();
};

/**
 * Gilt der Umsatz als endgueltig gebucht?
 *
 * BOOK ist gebucht. PDNG (vorgemerkt), INFO (nur zur Information, nicht auf
 * dem Konto gebucht) und FUTR (kuenftig) sind es nicht. Ohne Statusangabe
 * entscheidet die Dateiart: ein Kontoauszug fuehrt nur gebuchte Umsaetze, eine
 * Umsatzinformation auch vorgemerkte — dort reicht das Fehlen nicht.
 */
const isBooked = (entry: XmlElementLike, isReport: boolean): boolean => {
    const code = statusCodeOf(entry);
    return code === 'BOOK' || (code === '' && !isReport);
};

const NOT_PROVIDED = 'NOTPROVIDED';

const parseEntry = (
    entry: XmlElementLike,
    statementCurrency: string,
    seenContentKeys: Map<string, number>,
    isReport: boolean,
): BankTransaction[] => {
    const amountElement = childNamed(entry, 'Amt');
    if (!amountElement) {
        throw new Error('Ein Umsatz im Kontoauszug hat keinen Betrag (<Amt> fehlt).');
    }
    const currency = firstNonEmpty(amountElement.getAttribute('Ccy') ?? '', statementCurrency);
    if (!currency) {
        throw new Error('Ein Umsatz im Kontoauszug hat keine Währungsangabe.');
    }

    const indicator = textAt(entry, 'CdtDbtInd').toUpperCase();
    if (indicator !== 'CRDT' && indicator !== 'DBIT') {
        throw new Error(
            `Ein Umsatz im Kontoauszug hat keine gültige Richtung (CdtDbtInd = "${indicator}").`,
        );
    }
    // NUR CdtDbtInd bestimmt die Richtung. RvslInd sagt, WARUM gebucht wurde,
    // nicht WOHIN — die Bank stellt eine Ruecklastschrift bereits als DBIT
    // ein. Wer hier ein zweites Mal dreht, macht aus dem Abgang einen Eingang:
    // die geplatzte Lastschrift traegt den urspruenglichen Verwendungszweck,
    // der Abgleich schlaegt sie mit hoechster Sicherheit auf genau die
    // Rechnung vor, die gerade zurueckgegangen ist, die Rechnung gilt als
    // bezahlt und die Mahnung faellt aus.
    //
    // Gegenprobe im Modul selbst: balanceCents leitet das Saldo-Vorzeichen
    // ebenfalls allein aus CdtDbtInd ab. Beide Stellen widersprachen sich, und
    // Anfangssaldo + Summe der Umsaetze ging nicht auf den Endsaldo auf.
    const positive = indicator === 'CRDT';

    const bookingDate = dateFrom(childNamed(entry, 'BookgDt'), 'Buchungsdatum');
    const valueDateRaw = dateFrom(childNamed(entry, 'ValDt'), 'Wertstellung');
    const valueDate = firstNonEmpty(valueDateRaw, bookingDate);
    if (!valueDate) {
        throw new Error('Ein Umsatz im Kontoauszug hat weder Wertstellung noch Buchungsdatum.');
    }

    const booked = isBooked(entry, isReport);
    const entryReference = textAt(entry, 'AcctSvcrRef');
    const detailsList = childrenNamed(entry, 'NtryDtls').flatMap((node) =>
        childrenNamed(node, 'TxDtls'),
    );

    // Eine Sammelbuchung wird nur dann in Einzelposten zerlegt, wenn jeder
    // Posten seinen eigenen Betrag mitbringt. Sonst bliebe die Summe der Teile
    // hinter dem Buchungsbetrag zurueck und die Zuordnung waere falsch.
    const splittable =
        detailsList.length > 1 && detailsList.every((details) => !!childNamed(details, 'Amt'));
    const parts: Array<XmlElementLike | null> = splittable
        ? detailsList
        : [detailsList[0] ?? null];

    return parts.map((details) => {
        const partAmountElement = (splittable ? childNamed(details, 'Amt') : null) ?? amountElement;
        const magnitude = parseXmlAmount(textOf(partAmountElement), 'Umsatzbetrag');
        const amountCents = positive ? magnitude : -magnitude;

        const endToEndRaw = textAt(details, 'Refs', 'EndToEndId');
        const endToEndId =
            endToEndRaw && endToEndRaw.toUpperCase() !== NOT_PROVIDED ? endToEndRaw : '';
        const remittanceInfo = remittanceFrom(details, entry);
        const { name, iban } = counterpartyFrom(atPath(details, 'RltdPties'), amountCents > 0);

        // Bei einer zerlegten Sammelbuchung darf die Bankreferenz des Umsatzes
        // NICHT als Schluessel dienen: sie gilt fuer alle Teile gemeinsam und
        // wuerde die Teile in der Datenbank zu einer einzigen Zahlung
        // verschmelzen.
        const reference = firstNonEmpty(
            textAt(details, 'Refs', 'AcctSvcrRef'),
            splittable ? '' : entryReference,
        );

        const contentKey = [
            valueDate,
            String(amountCents),
            currency,
            endToEndId,
            remittanceInfo,
            iban,
            name,
        ].join('|');
        // Zwei inhaltlich identische Umsaetze in derselben Datei sind moeglich
        // (zweimal derselbe Betrag mit demselben Zweck am selben Tag). Ohne
        // Unterscheidung bekaemen sie denselben Schluessel und der zweite ginge
        // beim Import verloren. Der Zaehler haengt an der Reihenfolge in der
        // Datei und ist damit beim erneuten Hochladen derselbe.
        const seen = seenContentKeys.get(contentKey) ?? 0;
        seenContentKeys.set(contentKey, seen + 1);
        const uniqueKey = seen === 0 ? contentKey : `${contentKey}#${seen}`;

        return {
            bankTxId: reference || `camt-${stableTransactionHash(uniqueKey)}`,
            endToEndId: endToEndId || undefined,
            valueDate,
            bookingDate: bookingDate || undefined,
            amountCents,
            currency,
            remittanceInfo,
            counterpartyName: name || undefined,
            counterpartyIban: iban || undefined,
            booked,
            accountServicerReference: reference || undefined,
        };
    });
};

/** Saldo eines Auszugs zu einem der angegebenen Codes (OPBD/PRCD bzw. CLBD). */
const balanceCents = (statement: XmlElementLike, codes: string[]): Cents | undefined => {
    for (const balance of childrenNamed(statement, 'Bal')) {
        const code = textAt(balance, 'Tp', 'CdOrPrtry', 'Cd').toUpperCase();
        if (!codes.includes(code)) continue;
        const amount = childNamed(balance, 'Amt');
        if (!amount) continue;
        const magnitude = parseXmlAmount(textOf(amount), 'Saldo');
        return textAt(balance, 'CdtDbtInd').toUpperCase() === 'DBIT' ? -magnitude : magnitude;
    }
    return undefined;
};

/**
 * Liest einen CAMT.053-Kontoauszug oder eine CAMT.052-Umsatzinformation.
 *
 * Wirft bei allem, was kein verwertbarer Auszug ist. Ein leeres Ergebnis
 * zurueckzugeben waere hier die schlechtere Wahl: der Anwender wuerde eine
 * falsche Datei hochladen, "0 Umsätze" lesen und annehmen, es habe in dem
 * Zeitraum keine Zahlungen gegeben.
 */
export const parseCamt053 = (xml: string, options: Camt053Options = {}): BankStatement => {
    // Ein vorangestelltes BOM ueberlebt den Weg durch Bank-Portal und
    // Datei-Upload haeufig und wuerde sonst das Wurzelelement verstecken.
    const raw = xml ?? '';
    const source = (raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw).trim();
    if (!source) {
        throw new Error('Die Datei ist leer. Bitte den CAMT-Export (XML) der Bank hochladen.');
    }

    const parser = options.xmlParser ?? defaultXmlParser();
    let document: XmlDocumentLike;
    try {
        document = parser.parseFromString(source, 'application/xml');
    } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        throw new Error(`Die Datei konnte nicht als XML gelesen werden: ${detail}.`);
    }

    const root = document.documentElement;
    if (!root || hasParserError(root)) {
        throw new Error(
            'Die Datei konnte nicht als XML gelesen werden. Bitte prüfen, ob der Export vollständig heruntergeladen wurde.',
        );
    }
    if (root.localName !== 'Document') {
        throw new Error(
            `Die Datei ist kein CAMT-Kontoauszug (gefunden wurde <${root.localName}>).`,
        );
    }
    const namespace = root.namespaceURI ?? '';
    const declared053 = namespace.startsWith(CAMT053_NAMESPACE_PREFIX);
    const declared052 = namespace.startsWith(CAMT052_NAMESPACE_PREFIX);
    if (namespace && !declared053 && !declared052) {
        throw new Error(
            `Nicht unterstütztes Format: ${namespace}. Erwartet wird ein Kontoauszug camt.053.001.02/.08 oder eine Umsatzinformation camt.052.001.08.`,
        );
    }

    // Ohne Namensraum entscheidet die Huelle. Mit Namensraum muss sie dazu
    // passen — eine Datei, die sich als camt.052 ausgibt, aber einen
    // Kontoauszug enthaelt, ist kaputt oder falsch umbenannt.
    const statementContainer = declared052 ? null : childNamed(root, 'BkToCstmrStmt');
    const reportContainer = declared053 ? null : childNamed(root, 'BkToCstmrAcctRpt');
    const container = statementContainer ?? reportContainer;
    if (!container) {
        throw new Error(
            declared052
                ? 'Die Datei enthält keine Umsatzinformation (<BkToCstmrAcctRpt> fehlt).'
                : 'Die Datei enthält keinen Kontoauszug (<BkToCstmrStmt> fehlt). Formate wie camt.054 (Sammelavis) können hier nicht verarbeitet werden.',
        );
    }
    const isReport = container === reportContainer;

    const statements = childrenNamed(container, isReport ? 'Rpt' : 'Stmt');
    if (!statements.length) {
        throw new Error(
            isReport
                ? 'Die Umsatzinformation enthält keine Umsatzdaten (<Rpt> fehlt).'
                : 'Der Kontoauszug enthält keine Auszugsdaten (<Stmt> fehlt).',
        );
    }

    const ibans = statements
        .map((statement) => textAt(statement, 'Acct', 'Id', 'IBAN'))
        .filter((iban) => iban.length > 0);
    const uniqueIbans = Array.from(new Set(ibans));
    if (uniqueIbans.length > 1) {
        // Ein zusammengefasster Auszug ueber mehrere Konten haette einen
        // Anfangs- und Endsaldo, der zu keinem der Konten passt.
        throw new Error(
            `Die Datei enthält Auszüge zu mehreren Konten (${uniqueIbans.join(', ')}). Bitte je Konto einzeln importieren.`,
        );
    }

    const statementCurrency = firstNonEmpty(
        ...statements.map((statement) => textAt(statement, 'Acct', 'Ccy')),
    );

    // Ein gemeinsamer Zaehler ueber alle Auszuege der Datei: identische
    // Umsaetze duerfen sich auch ueber Auszugsgrenzen hinweg nicht denselben
    // Schluessel teilen.
    const seenContentKeys = new Map<string, number>();
    const transactions = statements.flatMap((statement) =>
        childrenNamed(statement, 'Ntry').flatMap((entry) =>
            parseEntry(entry, statementCurrency, seenContentKeys, isReport),
        ),
    );

    return {
        format: isReport ? 'camt.052' : 'camt.053',
        statementIds: statements.map((statement) => textAt(statement, 'Id')),
        iban: uniqueIbans[0],
        currency: statementCurrency || transactions[0]?.currency,
        createdAt: textAt(container, 'GrpHdr', 'CreDtTm') || undefined,
        openingBalanceCents: balanceCents(statements[0], ['OPBD', 'PRCD']),
        closingBalanceCents: balanceCents(statements[statements.length - 1], ['CLBD']),
        transactions,
    };
};
